import React, {useCallback, useEffect} from "react";
import {FormProvider, useForm, Controller} from "react-hook-form";
import {trackPromise, usePromiseTracker} from "react-promise-tracker";
import {Subject} from "rxjs";
import {showToast, SimpleDropdown} from "common";
import {useObservable} from "rxsupport";
import {FatOutlineButton} from "common/component/FatOutlineButton";
import {LoadingSpinner} from "common";
import {ReportModal} from "../../form-components/ReportModal";
import {ModalTextAreaInput} from "../../form-components/ModalTextAreaInput";
import {FsoSupportInput, YesNoOption, YesNoOptionMap} from "../../domain";
import {useSubmitFsoSupport} from "./useSubmitFsoSupport";

type Props = {
    visible$: Subject<boolean>;
    authItemGuids: string[];
};

export function FSOSupportFormModal({visible$, authItemGuids}: Props) {
    const visible = useObservable(visible$, false);
    const {submitFsoSupport} = useSubmitFsoSupport();

    const methods = useForm<FsoSupportInput>({
        defaultValues: {
            relatedToBilling: null,
            confirmedWithBilling: undefined,
            billingRejectionsCorrected: undefined,
            fsoInquiry: ""
        }
    });

    const {promiseInProgress: isCallingBackend} = usePromiseTracker();
    const {watch} = methods;
    const relatedToBilling = watch("relatedToBilling");
    const confirmedWithBilling = watch("confirmedWithBilling");


    // Reset conditional fields when relatedToBilling changes
    useEffect(() => {
        if (relatedToBilling !== YesNoOption.YES) {
            methods.setValue("confirmedWithBilling", undefined);
            methods.setValue("billingRejectionsCorrected", undefined);
        }
        if (!relatedToBilling) {
            methods.setValue("fsoInquiry", "");
        }
    }, [relatedToBilling, methods]);

    // Reset billingRejectionsCorrected when confirmedWithBilling changes
    useEffect(() => {
        if (confirmedWithBilling !== YesNoOption.YES) {
            methods.setValue("billingRejectionsCorrected", undefined);
        }
    }, [confirmedWithBilling, methods]);

    // Options for Yes/No dropdowns
    const yesNoOptions = YesNoOptionMap;

    const submitWithTracking = useCallback((formValues: FsoSupportInput) => 
        trackPromise(submitFsoSupport(authItemGuids, formValues)), 
        [submitFsoSupport, authItemGuids]
    );

    const onCancel = useCallback(async () => {
        showToast.warning(`Submission for FSO Inquiry is cancelled`);
        methods.reset();
        visible$.next(false);
    }, [methods, visible$]);

    const submit = useCallback(async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        await methods.handleSubmit(async (data: FsoSupportInput) => {
            try {
                await submitWithTracking(data);
                methods.reset();
                visible$.next(false);
                showToast.success("FSO Inquiry successfully submitted. The Request can be tracked in the “Submitted Requests” section and a request number ID will be available shortly");
            } catch (error) {
                const errorMessage = error instanceof Error ? error.message : "Unknown error occurred";
                showToast.error(`Failed to submit: ${errorMessage}`);
            }
        })(e);
    }, [methods, visible$, submitWithTracking]);

    return (
        <ReportModal
            visible={visible}
            title="FSO Inquiry"
            onClose={onCancel}
            maxWidth="max-w-2xl"
        >
            {!isCallingBackend ? (
                <FormProvider {...methods}>
                    <form autoComplete="off" onSubmit={submit}>
                        <div className="p-6">
                            {/* Related to Billing */}
                            <div className="mb-4">
                                <div className="mb-2">
                                    Is this related to billing?
                                </div>
                                <Controller
                                    name="relatedToBilling"
                                    control={methods.control}
                                    rules={{ required: "Required" }}
                                    render={(props: any) => (
                                        <div>
                                            <SimpleDropdown
                                                options={yesNoOptions}
                                                selected={props.field.value || ""}
                                                placeholder={'Please Choose'}
                                                setSelected={(selected) => {
                                                    methods.setValue("relatedToBilling", selected as YesNoOption | null);
                                                    methods.clearErrors("relatedToBilling");
                                                }}
                                                textColor={"black-200"}
                                                hasErrorBorder={!!props.fieldState.error}
                                            />
                                            {props.fieldState.error && (
                                                <div className="text-sm text-red-600 mt-1">
                                                    {props.fieldState.error.message}
                                                </div>
                                            )}
                                        </div>
                                    )} />
                            </div>

                            {/* Confirmed with billing - only show if relatedToBilling is "Yes" */}
                            {relatedToBilling === YesNoOption.YES && (
                                <div className="mb-4">
                                    <div className="mb-2">
                                        Have you confirmed with your billing if there were rejection(s)?
                                    </div>
                                    <Controller
                                        name="confirmedWithBilling"
                                        control={methods.control}
                                        rules={{ required: "Required" }}
                                        render={(props: any) => (
                                            <div>
                                                <SimpleDropdown
                                                    options={yesNoOptions}
                                                    selected={props.field.value || ""}
                                                    placeholder={'Please Choose'}
                                                    setSelected={(selected) => {
                                                        methods.setValue("confirmedWithBilling", selected as YesNoOption | null);
                                                        methods.clearErrors("confirmedWithBilling");
                                                    }}
                                                    textColor={"black-200"}
                                                    hasErrorBorder={!!props.fieldState.error}
                                                />
                                                {props.fieldState.error && (
                                                    <div className="text-sm text-red-600 mt-1">
                                                        {props.fieldState.error.message}
                                                    </div>
                                                )}
                                            </div>
                                        )} />
                                </div>
                            )}

                            {/* Warning message - show if confirmedWithBilling is "No" */}
                            {relatedToBilling === YesNoOption.YES && confirmedWithBilling === YesNoOption.NO && (
                                <div className="mb-4 p-4 border border-red-500 rounded-md bg-red-50">
                                    <p className="text-sm text-red-600">
                                        <strong>CONFIRM ACTION:</strong><br/>
                                        Confer with your organization&apos;s billing representatives to confirm minimum required billing was submitted and approved prior to the 15th
                                    </p>
                                </div>
                            )}

                            {/* Billing rejections corrected - only show if confirmedWithBilling is "Yes" */}
                            {relatedToBilling === YesNoOption.YES && confirmedWithBilling === YesNoOption.YES && (
                                <div className="mb-4">
                                    <div className="mb-2">
                                        Have billing rejections been corrected?
                                    </div>
                                    <Controller
                                        name="billingRejectionsCorrected"
                                        control={methods.control}
                                        rules={{ required: "Required" }}
                                        render={(props: any) => (
                                            <div>
                                                <SimpleDropdown
                                                    options={yesNoOptions}
                                                    selected={props.field.value || ""}
                                                    placeholder={'Please Choose'}
                                                    setSelected={(selected) => {
                                                        methods.setValue("billingRejectionsCorrected", selected as YesNoOption | null);
                                                        methods.clearErrors("billingRejectionsCorrected");
                                                    }}
                                                    textColor={"black-200"}
                                                    hasErrorBorder={!!props.fieldState.error}
                                                />
                                                {props.fieldState.error && (
                                                    <div className="text-sm text-red-600 mt-1">
                                                        {props.fieldState.error.message}
                                                    </div>
                                                )}
                                            </div>
                                        )} />
                                </div>
                            )}

                            {relatedToBilling === YesNoOption.YES
                                && confirmedWithBilling === YesNoOption.YES
                                && (
                                <div className="mb-4 p-4 border border-red-500 rounded-md bg-red-50">
                                    <p className="text-sm text-red-600 font-medium">
                                        <strong>CONFIRM ACTION:</strong><br/>
                                        Please respond with Full Offer Billing Pending if the hours that have been corrected meet minimum requirements.
                                    </p>
                                </div>
                            )}

                            {/* FSO Inquiry text field - show when relatedToBilling has a value */}
                            {relatedToBilling && (
                                <div className="mb-4">
                                    <ModalTextAreaInput
                                        disabled={isCallingBackend}
                                        field="fsoInquiry"
                                        label="How can we assist you?"
                                        placeholder="Enter your inquiry details here..."
                                        labelPosition="top"
                                        rowsNum={4}
                                        maxLength={4000}
                                        required
                                        requiredMessage="Please provide details for your FSO inquiry before submitting."
                                        showCharacterCount
                                    />
                                </div>
                            )}
                        </div>

                        {/* Footer */}
                        <div className="w-full flex border-t border-gray-200 p-4 justify-end">
                            <div className="flex w-1/3">
                                <FatOutlineButton
                                    color={'red'}
                                    text={"Cancel"}
                                    onClick={onCancel}
                                />
                                <span style={{width: 10}}/>
                                <FatOutlineButton
                                    type="submit"
                                    text={"Submit"}
                                    color={"green"}
                                />
                            </div>
                        </div>
                    </form>
                </FormProvider>
            ) : (
                <div className="p-24">
                    <LoadingSpinner text="Processing"/>
                </div>
            )}
        </ReportModal>
    );
}
